// ═══════════════════════════════════════════════════════════════
// PublicBrandingHeader — business identity strip at the top of
// the public estimate + invoice pages. Driven entirely by the
// fields snapshotted into the share/invoice doc at Send time
// (see lib/use-business-profile.js → businessSnapshot()).
//
// Defensive: if the mechanic hadn't filled in their profile yet
// when they sent the link, we render nothing rather than break
// the page. The customer still sees the rest of the estimate /
// invoice card as before.
// ═══════════════════════════════════════════════════════════════
import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';

function formatPhoneDisplay(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return raw || '';
}

export default function PublicBrandingHeader({ doc }) {
  if (!doc) return null;
  const {
    businessName,
    businessPhone,
    businessEmail,
    businessAddress,
    businessLogoUrl,
  } = doc;

  // Hide entirely if there's nothing to show — page falls back to the
  // generic "Estimate for…" eyebrow it had before this feature.
  const hasAny = !!(
    businessName || businessPhone || businessEmail ||
    businessAddress || businessLogoUrl
  );
  if (!hasAny) return null;

  const phoneDigits = String(businessPhone || '').replace(/\D/g, '');

  return (
    <header className="public-branding">
      {businessLogoUrl && (
        <div className="public-branding-logo">
          <img src={businessLogoUrl} alt={businessName ? `${businessName} logo` : 'Business logo'} />
        </div>
      )}
      <div className="public-branding-body">
        {businessName && <h2 className="public-branding-name">{businessName}</h2>}
        <div className="public-branding-meta">
          {businessPhone && (
            <a className="public-branding-link" href={phoneDigits ? `tel:${phoneDigits}` : undefined}>
              <Phone size={13} /> {formatPhoneDisplay(businessPhone)}
            </a>
          )}
          {businessEmail && (
            <a className="public-branding-link" href={`mailto:${businessEmail}`}>
              <Mail size={13} /> {businessEmail}
            </a>
          )}
          {businessAddress && (
            <span className="public-branding-link">
              <MapPin size={13} /> {businessAddress}
            </span>
          )}
        </div>
      </div>
    </header>
  );
}
